// ── Collateral Management — account-level Dashboard (pick an account, see its collateral trends) ──
import { useState, useEffect, useRef } from "react";
import { B } from "../../shared/theme";
import { fmt, fmtDate, fmtRatio } from "../../shared/format";
import { Btn, Card, ErrorMsg, SectionHeader, Spinner, Td, TdNum, TabBtn } from "../../shared/ui";
import { useApp } from "../../shared/appContext";
import { inventoryInsights } from "../../shared/inventoryInsights";
import { PairTrend, StackTrend, BarTrend, ArExcessTrend } from "./trendCharts";
import { ArTrendCard, ArRollforwardCard, ArQualityCard, InventoryTab, RowCapScroll } from "./collateralCards";
import { SlowMovingTab } from "./slowMovingTab";
import { ApAgingFindings, useApThresholds } from "./ApAgingFindings";
import { ArPeriodCard, ArRollCard } from "./arTrendCards";

// Invoice-date A/R aging buckets (header totals: ar_inv_*).
const AGING = [
  { key: "inv_0_30", label: "0–30", color: "#12b5db" },
  { key: "inv_31_60", label: "31–60", color: "#ffb20d" },
  { key: "inv_61_90", label: "61–90", color: "#e07c1a" },
  { key: "inv_91_120", label: "91–120", color: "#c15418" },
  { key: "inv_over120", label: ">120", color: "#993C1D" },
];
// A/P aging by DUE DATE (header totals: ap_dd_*).
const APAGING = [
  { key: "ap_dd_current", label: "Current", color: "#12b5db" },
  { key: "ap_dd_1_30", label: "1–30", color: "#ffb20d" },
  { key: "ap_dd_31_60", label: "31–60", color: "#e07c1a" },
  { key: "ap_dd_61_90", label: "61–90", color: "#c15418" },
  { key: "ap_dd_over90", label: ">90", color: "#993C1D" },
];
// A/R Trend — nested subsets of one measure → sequential cyan ramp (light → dark).
const TSER = [
  { id: "end", label: "Ending A/R", key: "ar_ending_balance", color: "#A5E3F1", w: 0.72 },
  { id: "elig", label: "Eligible A/R", key: "ar_total_eligible", color: "#12b5db", w: 0.48 },
  { id: "net", label: "Net Avail A/R", key: "ar_net_available", color: "#116588", w: 0.26 },
];
// Certificate Trend — key certificate figures over time (each a line; all dollars, one axis).
const CSER = [
  { key: "ar_beginning_balance", label: "Beginning A/R", color: "#A5E3F1" },
  { key: "ar_ending_balance", label: "Ending A/R", color: "#12b5db" },
  { key: "ar_total_ineligible", label: "Total A/R Ineligibles", color: "#993C1D" },
  { key: "ar_net_available", label: "Net Avail A/R", color: "#116588" },
  { key: "inv_gross", label: "Gross Inventory", color: "#ffb20d" },
  { key: "inv_available", label: "Avail Inventory", color: "#e07c1a" },
  { key: "re_available", label: "Real Estate Avail", color: "#6a4c93" },
  { key: "gross_collateral_avail", label: "Gross Collateral Avail", color: "#0F6E56" },
  { key: "loan_commitment", label: "Loan Commitment", color: "#8a5a00" },
  { key: "borrowing_base_avail", label: "Borrowing Base Avail", color: "#c15418" },
];
// Loan Rollforward Trend — previous balance should tie to the prior period's total-after-requested-funds (current_loan_balance).
const RSER = [
  { key: "previous_loan_balance", label: "Previous Balance", color: "#A5E3F1", w: 0.6 },
  { key: "total_after_requested", label: "Total After Req. Funds", color: "#116588", w: 0.34 },
];
// A/R Rollforward Trend — Beginning A/R vs Total Ending A/R (activity pieces shown in the table).
const ARSER = [
  { key: "ar_beginning_balance", label: "Beginning A/R", color: "#A5E3F1", w: 0.6 },
  { key: "ar_ending_balance", label: "Total Ending A/R", color: "#116588", w: 0.34 },
];
// Categorical palette for the Top-10 Debtors / Top-10 Vendors multi-line trends.
const PARTY_COLORS = ["#12b5db", "#ffb20d", "#0F6E56", "#993C1D", "#6a4c93", "#e07c1a", "#116588", "#c15418", "#8a5a00", "#5A8F3C"];
// A/P by-vendor due-date aging buckets (byVendorAP dd_* columns).
const VBKT = [
  { key: "dd_current", label: "Current" },
  { key: "dd_1_30", label: "1-30" },
  { key: "dd_31_60", label: "31-60" },
  { key: "dd_61_90", label: "61-90" },
  { key: "dd_over90", label: ">90" },
];
const iso = d => d ? String(d).slice(0, 10) : "";
// ── Inventory ──────────────────────────────────────────────────────────────
// NESTED, NOT PARTITIONED: gross contains eligible contains net available.
// Drawn as bars inside bars, never stacked — stacking would show roughly three
// times the inventory that exists.
const INVSER = [
  { key: "inv_gross",          label: "Gross (at cost)", color: "#A5E3F1", w: 1.00 },
  { key: "inv_total_eligible", label: "Eligible",        color: "#12b5db", w: 0.68 },
  { key: "inv_net_available",  label: "Net available",   color: "#116588", w: 0.38 },
];
// The six buckets bbcParser's invNorm() collapses borrower wording into. Fixed
// colours so a category keeps its colour across certificates.
// The four measures a category carries, each a step down the same ladder:
// gross, less ineligible, times the advance rate, is what that category makes
// available. Offered as a switch rather than four charts because the SHAPE is
// the point — a mix that looks balanced at gross and lopsided at available is
// telling you which category the ineligibles and advance rates are eating.
//
// NOTE the last one is the CATEGORY's available, not the header's net
// available. Sublimit and reserve are caps on inventory as a whole and cannot
// be attributed to a category, so they are not in this chart. The table names
// both so the difference is visible rather than a discrepancy someone chases.
const INVMEAS = [
  { key: "gross",      label: "Gross",      header: "inv_gross" },
  { key: "ineligible", label: "Ineligible", header: "inv_total_ineligible" },
  { key: "eligible",   label: "Eligible",   header: "inv_total_eligible" },
  { key: "available",  label: "Available",  header: "inv_available" },
];

const INVCAT = [
  { key: "RM",                 label: "Raw material",   color: "#A5E3F1" },
  { key: "WIP",                label: "WIP",            color: "#7cd4eb" },
  { key: "FG",                 label: "Finished goods", color: "#12b5db" },
  { key: "Packaging/Supplies", label: "Packaging",      color: "#ffb20d" },
  { key: "General",            label: "General",        color: "#e07c1a" },
  { key: "Other",              label: "Other",          color: "#993C1D" },
];

const fmtShort = v => { const a = Math.abs(v); return (a >= 1e6 ? (v / 1e6).toFixed(1) + "M" : a >= 1e3 ? Math.round(v / 1e3) + "K" : String(Math.round(v))); };
const monthLbl = d => { const s = String(d || "").slice(0, 10); const p = s.split("-"); return p.length >= 3 ? p[1] + "/" + p[2] + "/" + p[0].slice(2) : (p.length >= 2 ? p[1] + "/" + p[0].slice(2) : s); };

// Last borrower viewed, per browser. Storage can be unavailable (private
// window, blocked site data) — then we simply fall back to the newest BBC.
const LAST_ACCT_KEY = "ace.collateral.lastAccount";
const readLastAcct = () => { try { return window.localStorage.getItem(LAST_ACCT_KEY) || ""; } catch (e) { return ""; } };
const writeLastAcct = v => { try { window.localStorage.setItem(LAST_ACCT_KEY, v); } catch (e) { /* ignore */ } };

export const CollateralDashboard = () => {
  const { api, go, view } = useApp();
  const [accounts, setAccounts] = useState([]);
  // Opens on: the borrower passed in (clicked on the Portfolio page) → the one
  // last viewed here → the borrower with the newest BBC. Never empty when there
  // is anything to show.
  const [acct, setAcct] = useState(() => (view && view.accountid) || readLastAcct());
  useEffect(() => { if (view && view.accountid) setAcct(view.accountid); }, [view && view.accountid]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (acct) writeLastAcct(acct); }, [acct]);
  useEffect(() => {
    if (acct) return;
    api.get("getCollateralPortfolio")
      .then(d => {
        const newest = (Array.isArray(d) ? d : []).filter(r => ["Accrual", "Non Accrual"].includes(r.accountstatus))
          .sort((a, b) => String(b.asofdate || "").localeCompare(String(a.asofdate || "")))[0];
        if (newest) setAcct(prev => prev || newest.accountid);
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [hist, setHist] = useState([]);
  const apTh = useApThresholds();   // A/P aging "What changed" (shared/apInsights.js)
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  // trend toggles (same defaults as the BBC page)
  const [trendOn, setTrendOn] = useState({ end: true, elig: true, net: true, excess: true });
  const [trendApOn, setTrendApOn] = useState({ ap_dd_current: true, ap_dd_1_30: true, ap_dd_31_60: true, ap_dd_61_90: true, ap_dd_over90: true });
  const [trendArAgeOn, setTrendArAgeOn] = useState({ inv_0_30: true, inv_31_60: true, inv_61_90: true, inv_91_120: true, inv_over120: true });
  const [certShow, setCertShow] = useState(() => CSER.reduce((o, s) => ({ ...o, [s.key]: true }), {}));   // which Certificate Trend panels to show
  const [tab, setTab] = useState("overview");   // Overview | A/R Detail | A/P Detail (same as the BBC page)
  const [partyTrend, setPartyTrend] = useState({ debtors: [], vendors: [], skus: [] });
  const [topDebtorOff, setTopDebtorOff] = useState({});
  const [topVendorOff, setTopVendorOff] = useState({});
  // Its own endpoint: getBorrowingBases returns only inv_gross and
  // inv_available on the list query, so eligible and net available — the two
  // that say what inventory CONTRIBUTES — are not in `hist` at all.
  const [invTrend, setInvTrend] = useState({ header: [], categories: [] });
  const [invSerOn, setInvSerOn] = useState({ inv_gross: true, inv_total_eligible: true, inv_net_available: true });
  const [invCatOff, setInvCatOff] = useState({});
  const [invMeas, setInvMeas] = useState("gross");
  const [latestDetail, setLatestDetail] = useState(null);   // full detail (byDebtor / byVendorAP) of the most recent BBC
  const [dbDesig, setDbDesig] = useState("");   // designation (source tab) filter — Debtors table
  const [vnDesig, setVnDesig] = useState("");   // designation filter — A/P by Vendor table

  // funded accounts only (Accrual / Non Accrual), alphabetical
  useEffect(() => {
    api.get("getAccounts")
      .then(d => setAccounts((Array.isArray(d) ? d : [])
        .filter(a => ["Accrual", "Non Accrual"].includes(a.accountstatus))
        .sort((a, b) => (a.companyname || "").localeCompare(b.companyname || ""))))
      .catch(() => setAccounts([]));
  }, []);

  // load the account's borrowing-base history (newest as-of first) + per-debtor/vendor party trend
  useEffect(() => {
    if (!acct) { setHist([]); setPartyTrend({ debtors: [], vendors: [], skus: [] });
                 setInvTrend({ header: [], categories: [] }); return; }
    setLoading(true); setErr(null);
    api.get("getBorrowingBases", { accountid: acct })
      .then(d => { setHist(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
    api.get("getCollateralPartyTrend?accountid=" + acct)
      .then(p => setPartyTrend({ debtors: Array.isArray(p?.debtors) ? p.debtors : [], vendors: Array.isArray(p?.vendors) ? p.vendors : [],
                                skus: Array.isArray(p?.skus) ? p.skus : [] }))
      .catch(() => setPartyTrend({ debtors: [], vendors: [], skus: [] }));
    api.get("getInventoryTrend?accountid=" + acct)
      .then(t => setInvTrend({
        header: Array.isArray(t?.header) ? t.header : [],
        categories: Array.isArray(t?.categories) ? t.categories : [],
      }))
      .catch(() => setInvTrend({ header: [], categories: [] }));
  }, [acct]);

  // Slow Moving tab: the most recent certificate that carried a slow-moving
  // sheet (not necessarily the latest certificate — many borrowers send it less
  // often), and its SKU lines.
  const smCert = hist.find(r => parseInt(r.sm_sku_count, 10) > 0) || null;
  const [smLines, setSmLines] = useState(null);
  useEffect(() => {
    if (!smCert) { setSmLines(null); return; }
    setSmLines(null);
    api.get("getBorrowingBaseSlowMoving", { id: smCert.id })
      .then(x => setSmLines(Array.isArray(x) ? x : []))
      .catch(() => setSmLines([]));
  }, [smCert && smCert.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // most recent BBC's detail (Debtors + A/P by Vendor tables)
  useEffect(() => {
    const id = hist[0]?.id;
    if (!id) { setLatestDetail(null); return; }
    api.get("getBorrowingBase", { id }).then(setLatestDetail).catch(() => setLatestDetail(null));
  }, [hist]);

  const cardTitle = { fontSize: 11, fontWeight: 700, color: B.cyanDark, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10, paddingBottom: 6, borderBottom: "1.5px solid " + B.cyanLight };
  const row = { display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 6 };
  // ── inventory over time ──────────────────────────────────────────────────
  const invHist    = invTrend.header;
  const invLatest  = invHist.length ? invHist[invHist.length - 1] : null;
  const invCatRows = invTrend.categories;
  const invCatsPresent = INVCAT.filter(b =>
    invCatRows.some(c => c.category_norm === b.key && (parseFloat(c.gross) || 0) !== 0));
  // Measure is a parameter, not a second function. Four near-identical readers
  // is four places for one of them to keep pointing at gross after the others
  // moved on.
  const invCatAt = (bucket, bbid, meas) => {
    const r = invCatRows.find(c => c.category_norm === bucket && c.bbid === bbid);
    return r ? (parseFloat(r[meas || invMeas]) || 0) : 0;
  };
  const invMeasDef = INVMEAS.find(m => m.key === invMeas) || INVMEAS[0];
  const invCatShown = invCatsPresent.filter(b => !invCatOff[b.key]);
  // Whether this borrower has inventory in the deal at all. A pure A/R facility
  // should not be offered a tab that can only ever show zeroes.
  const hasInvTab = invHist.some(r => (parseFloat(r.inv_gross) || 0) !== 0) || invCatsPresent.length > 0;
  // Recomputed from the trend payload rather than stored. It is cheap, and a
  // cached finding that outlives the data it described is worse than none.
  const invFindings = inventoryInsights(invTrend);

  // Headers wrap; data cells don't. width "1%" makes each column only as wide as
  // its data needs, so a long label ("Total Loan Balance After Requested Funds")
  // breaks onto two or three lines instead of setting the column width.
  // Bottom-aligned so short and wrapped labels sit on the same baseline.
  const thSticky = { padding: "8px 10px", fontSize: 12, fontWeight: 600, color: B.cyanDark, background: "#ffffff", whiteSpace: "normal", width: "1%", minWidth: 64, lineHeight: 1.25, verticalAlign: "bottom", position: "sticky", top: 0, zIndex: 3, borderBottom: "2px solid " + B.cyan, boxShadow: "0 2px 4px rgba(0,0,0,0.06)" };

  // page-local aliases so the extracted BBC card blocks work verbatim
  const latest = hist[0] || null;
  const h = latest || {};
  const hasAP = hist.some(r => r.ap_open_balance != null && parseFloat(r.ap_open_balance) !== 0);

  const acctName = accounts.find(a => String(a.id) === String(acct))?.companyname;

  // latest-period aging snapshots (header totals on the newest BBC)
  const arSnap = AGING.reduce((s, b) => s + (parseFloat(latest?.["ar_" + b.key]) || 0), 0);
  const apSnap = APAGING.reduce((s, b) => s + (parseFloat(latest?.[b.key]) || 0), 0);

  const kpis = latest ? [
    { label: "Ending A/R", v: latest.ar_ending_balance },
    { label: "Net Avail A/R", v: latest.ar_net_available },
    { label: "Borrowing Base Avail", v: latest.borrowing_base_avail },
    { label: "Loan Balance", v: latest.current_loan_balance },
    { label: "Excess / (Deficit)", v: latest.excess_availability, signed: true },
  ] : [];

  // Loan-rollforward pieces (Less: Collections / Loan Paydowns / Adjustments, …) captured from the certificate's rollforward section.
  // Requested Funds gets its own dedicated column, so it's excluded from the generic piece columns.
  const parseRoll = r => { try { return r.rollforward_json ? JSON.parse(r.rollforward_json) : []; } catch (e) { return []; } };
  const RE_REQFUNDS = /requested\s*(fund|advance)/i;
  const RE_LOANADV = /loan advance/i;   // "(A) Loan Advances since prior BBC" — used in the ties check
  const isRollPiece = lbl => { const s = String(lbl || "").trim(); return !/^(previous loan balance|current loan balance|total loan balance|excess availability)/i.test(s) && !RE_REQFUNDS.test(s); };
  const rollReqFunds = r => { const p = parseRoll(r).find(p => RE_REQFUNDS.test(String(p.label || ""))); return p ? p.value : null; };
  const rollLoanAdv = r => { const p = parseRoll(r).find(p => RE_LOANADV.test(String(p.label || ""))); return p ? p.value : null; };
  const rollPieceLabels = [];
  hist.forEach(r => parseRoll(r).forEach(p => { if (isRollPiece(p.label) && !rollPieceLabels.includes(p.label)) rollPieceLabels.push(p.label); }));

  // A/R rollforward pieces (Gross Sales, Debit Memos, Collections, Discounts, Credit Memos, Other/Timing) between Beginning and Total Ending A/R.
  const parseArRoll = r => { try { return r.ar_rollforward_json ? JSON.parse(r.ar_rollforward_json) : []; } catch (e) { return []; } };
  const isArRollPiece = lbl => !/^(beginning a\/r balance|total ending a\/r balance)/i.test(String(lbl || "").trim());
  const arRollLabels = [];
  hist.forEach(r => parseArRoll(r).forEach(p => { if (isArRollPiece(p.label) && !arRollLabels.includes(p.label)) arRollLabels.push(p.label); }));

  // ── Top-10 party trends (top 10 chosen from the most recent BBC, then trended across history) ──
  const buildParty = rows => {
    const byDate = {}; const dset = new Set();
    (rows || []).forEach(r => { const d = iso(r.asofdate); if (!d) return; dset.add(d); (byDate[d] = byDate[d] || {}); byDate[d][r.consolidatedname] = (byDate[d][r.consolidatedname] || 0) + (parseFloat(r.open_balance) || 0); });
    const dates = [...dset].sort();
    const last = dates[dates.length - 1];
    const top = last ? Object.entries(byDate[last]).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([n]) => n) : [];
    return { dates, byDate, top };
  };
  const debtorTrend = buildParty(partyTrend.debtors);
  const vendorTrend = buildParty(partyTrend.vendors);

  // Detail tables from the most recent BBC.
  const byDebtor = latestDetail?.byDebtor || [];
  const byVendorAP = latestDetail?.byVendorAP || [];
  const dbDesigs = [...new Set(byDebtor.map(d => d.designation).filter(Boolean))].sort();
  const shownDebtors = dbDesig ? byDebtor.filter(d => (d.designation || "") === dbDesig) : byDebtor;
  const vnDesigs = [...new Set(byVendorAP.map(d => d.designation).filter(Boolean))].sort();
  const shownVendors = vnDesig ? byVendorAP.filter(d => (d.designation || "") === vnDesig) : byVendorAP;
  const desigSelStyle = { padding: "5px 9px", fontSize: 12, borderRadius: 6, border: "0.5px solid var(--color-border-secondary)", background: "var(--color-background-primary)", color: "var(--color-text-primary)" };
  const goldTot = { fontWeight: 700, color: "#603700" };

  const partyTrendCard = (title, td, offState, setOffState, grandLabel) => {
    if (!td || td.dates.length < 2 || td.top.length === 0) return null;
    const asc = td.dates;
    const val = (d, n) => td.byDate[d]?.[n] || 0;
    const grandOf = d => Object.values(td.byDate[d] || {}).reduce((s, v) => s + (parseFloat(v) || 0), 0);
    const gmax = Math.max(1, ...asc.flatMap(d => td.top.map(nm => val(d, nm))));
    const curIso = iso(latest?.asofdate);
    return (
      <Card style={{ overflowX: "auto", marginBottom: 16 }}>
        <div style={{ ...cardTitle, padding: "12px 12px 8px", marginBottom: 0 }}>{title} <span style={{ fontWeight: 400, color: "var(--color-text-secondary)" }}>(top {td.top.length} as of {fmtDate(asc[asc.length - 1])} · {asc.length} periods · Δ vs prior BBC)</span></div>
        <div style={{ padding: "10px 12px 6px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12 }}>
            {td.top.map(nm => {
              const vals = asc.map(d => val(d, nm));
              const last = vals[vals.length - 1], prev = vals[vals.length - 2];
              const dlt = last - prev, pct = prev ? (dlt / prev * 100) : 0, flat = Math.abs(pct) < 1;
              const col = flat ? "var(--color-text-secondary)" : (dlt > 0 ? "#993C1D" : "#0F6E56");
              const bg = flat ? "#eef1f4" : (dlt > 0 ? "#FBECE7" : "#E1F5EE");
              const SW = 168, SH = 46, sp = 5;
              const sx = i => sp + (SW - 2 * sp) * (vals.length > 1 ? i / (vals.length - 1) : 0.5);
              const sy = v => SH - sp - (SH - 2 * sp) * (v / gmax);
              const pts = vals.map((v, i) => sx(i) + "," + sy(v)).join(" ");
              return (
                <div key={nm} style={{ border: "0.5px solid var(--color-border-tertiary)", borderRadius: 8, padding: "9px 11px" }}>
                  <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={nm}>{nm}</div>
                  <svg width="100%" viewBox={`0 0 ${SW} ${SH}`} style={{ display: "block", margin: "4px 0" }}>
                    <polygon points={`${sp},${SH - sp} ${pts} ${SW - sp},${SH - sp}`} fill={B.cyan} opacity="0.08" />
                    <polyline points={pts} fill="none" stroke={B.cyan} strokeWidth="1.6" />
                    <circle cx={sx(vals.length - 1)} cy={sy(last)} r="2.8" fill={flat ? B.cyanDark : col} />
                  </svg>
                  <div style={{ fontSize: 15, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{fmt(last)}</div>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: "1px 7px", borderRadius: 10, display: "inline-block", marginTop: 3, color: col, background: bg }}>
                    {flat ? "–" : (dlt > 0 ? "▲" : "▼")} {dlt >= 0 ? "+" : "−"}{fmtShort(Math.abs(dlt))} · {dlt >= 0 ? "+" : "−"}{Math.abs(pct).toFixed(0)}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        <div style={{ maxHeight: 240, overflowY: "auto", overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, whiteSpace: "nowrap" }}>
            <thead><tr>
              <th style={{ ...thSticky, textAlign: "left" }}>As-of</th>
              {td.top.map((nm, i) => <th key={nm} style={{ ...thSticky, textAlign: "right" }}><span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: PARTY_COLORS[i % PARTY_COLORS.length], display: "inline-block" }} />{nm}</span></th>)}
              <th style={{ ...thSticky, textAlign: "right" }}>Top {td.top.length} Total</th>
              <th style={{ ...thSticky, textAlign: "right" }}>{grandLabel}</th>
            </tr></thead>
            <tbody>
              {asc.slice().reverse().map(d => {
                const tot = td.top.reduce((s, nm) => s + val(d, nm), 0);
                const grand = grandOf(d);
                const pctSpan = v => <span style={{ fontSize: 10, color: "var(--color-text-secondary)" }}> ({grand ? (v / grand * 100).toFixed(1) : "0.0"}%)</span>;
                return (
                  <tr key={d} style={{ background: curIso === d ? B.cyanLight : "" }}>
                    <Td style={{ fontWeight: curIso === d ? 600 : 400 }}>{fmtDate(d)}</Td>
                    {td.top.map(nm => { const v = val(d, nm); return <TdNum key={nm}>{fmt(v)}{pctSpan(v)}</TdNum>; })}
                    <TdNum style={{ fontWeight: 600 }}>{fmt(tot)}{pctSpan(tot)}</TdNum>
                    <TdNum style={{ fontWeight: 600 }}>{fmt(grand)}</TdNum>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    );
  };

  // Certificate Trend small multiples — one sparkline panel per certificate figure (each scaled to its own range,
  // since the metrics differ wildly in magnitude), with current value + Δ vs prior month. Panels are user-selectable.
  const certSmallMultiples = () => {
    const asc = hist.slice().reverse();   // oldest → newest
    const shown = CSER.filter(s => certShow[s.key]);
    return (
      <>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
          {CSER.map(s => (
            <button key={s.key} onClick={() => setCertShow(o => ({ ...o, [s.key]: !o[s.key] }))}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 8px", borderRadius: 12, border: "0.5px solid var(--color-border-secondary)", background: "none", cursor: "pointer", fontSize: 11, color: "var(--color-text-secondary)", opacity: certShow[s.key] ? 1 : 0.4 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: s.color, display: "inline-block" }} />
              {s.label}
            </button>
          ))}
        </div>
        {shown.length === 0
          ? <div style={{ fontSize: 12, color: "var(--color-text-secondary)", padding: "12px 0" }}>No panels selected — pick one or more figures above.</div>
          : <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12 }}>
            {shown.map(s => {
              const vals = asc.map(r => parseFloat(r[s.key]));
              const fin = vals.filter(v => isFinite(v));
              const last = vals[vals.length - 1], prev = vals[vals.length - 2];
              const hasLast = isFinite(last), hasPrev = isFinite(prev);
              const dlt = (hasLast && hasPrev) ? last - prev : null;
              const pct = (dlt != null && prev) ? (dlt / prev * 100) : null;
              const flat = pct != null && Math.abs(pct) < 1;
              const col = dlt == null || flat ? "var(--color-text-secondary)" : (dlt > 0 ? "#993C1D" : "#0F6E56");
              const bg = dlt == null || flat ? "#eef1f4" : (dlt > 0 ? "#FBECE7" : "#E1F5EE");
              const mn = fin.length ? Math.min(...fin) : 0, mx = fin.length ? Math.max(...fin) : 1, rng = (mx - mn) || 1;
              const SW = 168, SH = 46, sp = 5;
              const sx = i => sp + (SW - 2 * sp) * (vals.length > 1 ? i / (vals.length - 1) : 0.5);
              const sy = v => SH - sp - (SH - 2 * sp) * ((v - mn) / rng);
              const pts = vals.map((v, i) => isFinite(v) ? sx(i) + "," + sy(v) : null).filter(Boolean).join(" ");
              return (
                <div key={s.key} style={{ border: "0.5px solid var(--color-border-tertiary)", borderRadius: 8, padding: "9px 11px" }}>
                  <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={s.label}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color, display: "inline-block", marginRight: 6 }} />{s.label}
                  </div>
                  <svg width="100%" viewBox={`0 0 ${SW} ${SH}`} style={{ display: "block", margin: "4px 0" }}>
                    {fin.length > 1 && <polyline points={pts} fill="none" stroke={s.color} strokeWidth="1.8" />}
                    {hasLast && <circle cx={sx(vals.length - 1)} cy={sy(last)} r="2.8" fill={dlt == null || flat ? s.color : col} />}
                  </svg>
                  <div style={{ fontSize: 15, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{hasLast ? fmt(last) : "—"}</div>
                  {dlt != null
                    ? <span style={{ fontSize: 11, fontWeight: 600, padding: "1px 7px", borderRadius: 10, display: "inline-block", marginTop: 3, color: col, background: bg }}>
                        {flat ? "–" : (dlt > 0 ? "▲" : "▼")} {dlt >= 0 ? "+" : "−"}{fmtShort(Math.abs(dlt))}{pct != null ? ` · ${dlt >= 0 ? "+" : "−"}${Math.abs(pct).toFixed(0)}%` : ""}
                      </span>
                    : <span style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 3, display: "inline-block" }}>no prior period</span>}
                </div>
              );
            })}
          </div>}
      </>
    );
  };

  return <>
    <SectionHeader title="Borrower Dashboard" actions={[
      <Btn key="back" onClick={() => go("collateral-portfolio")}>← Portfolio</Btn>,
      <select key="acct" value={acct} onChange={e => setAcct(e.target.value)}
        style={{ padding: "6px 12px", fontSize: 13, borderRadius: 6, border: "0.5px solid var(--color-border-secondary)", background: "var(--color-background-primary)", color: "var(--color-text-primary)", minWidth: 240 }}>
        <option value="">— Select an account —</option>
        {accounts.map(a => <option key={a.id} value={a.id}>{a.companyname}{a.accountnumber ? ` (${a.accountnumber})` : ""}</option>)}
      </select>,
      // Most recent certificate for this borrower — open it in ACE, or the signed
      // copy in Box (same "View Online" link the certificate page has).
      ...(latest ? [
        <Btn key="latest" onClick={() => go("collateral-bbc", { bbid: latest.id })}>Open Latest BBC · {fmtDate(latest.asofdate)}</Btn>,
        ...(latest.box_url ? [<a key="box" href={latest.box_url} target="_blank" rel="noopener noreferrer"
             style={{ display: "inline-flex", alignItems: "center", padding: "6px 12px", borderRadius: 6, fontSize: 13, fontWeight: 500,
                      textDecoration: "none", border: "0.5px solid " + B.cyan, color: B.cyanDark }}>View Online</a>] : []),
      ] : []),
    ]} />

    {!acct
      ? <Card style={{ padding: 40, textAlign: "center", color: "var(--color-text-secondary)", fontSize: 14 }}>
          Select an account above to see its borrowing-base collateral trends.
        </Card>
      : loading ? <Spinner />
      : err ? <ErrorMsg msg={err} onRetry={() => setAcct(acct)} />
      : hist.length === 0
      ? <Card style={{ padding: 40, textAlign: "center", color: "var(--color-text-secondary)", fontSize: 14 }}>
          No borrowing bases on file for {acctName || "this account"}.
        </Card>
      : <>
        <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginTop: -8, marginBottom: 16 }}>
          <b style={{ color: "var(--color-text-primary)" }}>{acctName}</b> · {hist.length} borrowing base{hist.length !== 1 ? "s" : ""} · latest as of <b style={{ color: "var(--color-text-primary)" }}>{fmtDate(latest.asofdate)}</b>
        </div>

        {/* Latest-period KPIs */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 16 }}>
          {kpis.map(k => {
            const val = parseFloat(k.v);
            return (
              <Card key={k.label} style={{ padding: "14px 16px" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>{k.label}</div>
                <div style={{ fontSize: 20, fontWeight: 700, fontVariantNumeric: "tabular-nums", color: k.signed && val < 0 ? "#993C1D" : "var(--color-text-primary)" }}>{fmt(k.v || 0)}</div>
              </Card>
            );
          })}
        </div>

        {/* ── Tabs (same structure/breakouts as the BBC page) ── */}
        <div style={{ marginTop: 8 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 3, position: "relative", zIndex: 1, marginTop: 4, borderBottom: "4px solid " + B.cyan }}>
          {[["overview", "Overview"], ["ar", "A/R Detail"], ["ap", "A/P Detail"],
      ...(hasInvTab ? [["inv", "Inventory"]] : []),
      ...(smCert ? [["sm", "Slow Moving Inventory"]] : [])].map(([k, lbl]) => (
            <TabBtn key={k} id={k} current={tab} label={lbl} onClick={setTab} />
          ))}
        </div>
        <div style={{ background: "var(--color-background-primary)", border: "0.5px solid var(--color-border-tertiary)", borderTop: "none", borderRadius: "0 0 8px 8px", padding: 16, position: "relative", zIndex: 2 }}>

        {/* ══════════ OVERVIEW — Certificate Trend · A/R Trend · Loan Rollforward ══════════ */}
        {tab === "overview" && (<>
                {/* Certificate Trend — key certificate figures over time (multi-line; toggle series) */}
        {hist.length > 1 && (
          <Card style={{ marginBottom: 16 }}>
            <div style={{ ...cardTitle, padding: "12px 12px 8px", marginBottom: 0 }}>Certificate Trend <span style={{ fontWeight: 400, color: "var(--color-text-secondary)" }}>({hist.length})</span></div>
            <div style={{ padding: "8px 12px 10px" }}>
              {certSmallMultiples()}
            </div>
            <div style={{ maxHeight: 260, overflowY: "auto", overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, whiteSpace: "nowrap" }}>
                <thead><tr>
                  <th style={{ ...thSticky, textAlign: "left" }}>As-of</th>
                  {CSER.map(s => <th key={s.key} style={{ ...thSticky, textAlign: "right" }}>{s.label}</th>)}
                </tr></thead>
                <tbody>
                  {hist.map(r => (
                    <tr key={r.id} style={{ cursor: "pointer", background: r.id === h.id ? B.cyanLight : "" }} onClick={() => go("collateral-bbc", { bbid: r.id })}>
                      <Td style={{ fontWeight: r.id === h.id ? 600 : 400 }}>{fmtDate(r.asofdate)}</Td>
                      {CSER.map(s => { const v = r[s.key]; return <TdNum key={s.key} style={{ color: v == null ? "var(--color-text-tertiary)" : "inherit" }}>{v == null ? "—" : fmt(v)}</TdNum>; })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

                {/* Loan Rollforward Trend — previous balance ties to the prior period's total-after-requested-funds */}
        {hist.length > 1 && (
          <Card style={{ marginBottom: 16 }}>
            <div style={{ ...cardTitle, padding: "12px 12px 8px", marginBottom: 0 }}>Loan Rollforward Trend <span style={{ fontWeight: 400, color: "var(--color-text-secondary)" }}>({hist.length})</span></div>
            <div style={{ padding: "8px 12px 4px" }}>
              <PairTrend key={"rf-" + acct} hist={hist} series={RSER} />
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 4 }}>
                {RSER.map(s => (
                  <span key={s.key} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--color-text-secondary)" }}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: s.color, display: "inline-block" }} />{s.label}
                  </span>
                ))}
              </div>
            </div>
            <div style={{ maxHeight: 240, overflowY: "auto", overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, whiteSpace: "nowrap" }}>
                <thead><tr>
                  <th style={{ ...thSticky, textAlign: "left" }}>As-of</th>
                  <th style={{ ...thSticky, textAlign: "right" }}>Previous Balance</th>
                  {rollPieceLabels.map(lbl => <th key={lbl} style={{ ...thSticky, textAlign: "right" }}>{lbl}</th>)}
                  <th style={{ ...thSticky, textAlign: "right" }}>Net Change</th>
                  <th style={{ ...thSticky, textAlign: "right" }}>Current Loan Balance</th>
                  <th style={{ ...thSticky, textAlign: "right" }}>Prior Ending</th>
                  <th style={{ ...thSticky, textAlign: "right" }}>Requested Funds</th>
                  <th style={{ ...thSticky, textAlign: "right" }}>Total Loan Balance After Requested Funds</th>
                  <th style={{ ...thSticky, textAlign: "right" }}>Excess Availability</th>
                  <th style={{ ...thSticky, textAlign: "center" }}>Ties?</th>
                </tr></thead>
                <tbody>
                  {hist.map((r, i) => {
                    const prev = r.previous_loan_balance;
                    const curBal = r.current_loan_balance;                    // Current Loan Balance (before the new draw)
                    const totalAfter = r.total_after_requested;               // Total Loan Balance After Requested Funds (the true ending)
                    const priorRow = hist[i + 1];                             // hist is newest-first → next entry is the prior period
                    const priorEnd = priorRow ? (priorRow.total_after_requested != null ? priorRow.total_after_requested : priorRow.current_loan_balance) : null;
                    const net = (prev != null && curBal != null) ? (parseFloat(curBal) - parseFloat(prev)) : null;
                    const loanAdv = rollLoanAdv(r);                           // (A) Loan Advances since prior BBC
                    // Ties?: Previous Balance + Loan Advances since prior BBC should equal the prior period's ending.
                    const tieExpected = (prev != null) ? (parseFloat(prev) + (loanAdv != null ? parseFloat(loanAdv) : 0)) : null;
                    const tie = (tieExpected != null && priorEnd != null) ? Math.abs(tieExpected - parseFloat(priorEnd)) < 0.5 : null;
                    const pm = {}; parseRoll(r).forEach(p => { pm[p.label] = p.value; });
                    const reqFunds = rollReqFunds(r);
                    const exc = r.excess_availability;
                    return (
                      <tr key={r.id} style={{ cursor: "pointer", background: r.id === h.id ? B.cyanLight : "" }} onClick={() => go("collateral-bbc", { bbid: r.id })}>
                        <Td style={{ fontWeight: r.id === h.id ? 600 : 400 }}>{fmtDate(r.asofdate)}</Td>
                        <TdNum>{prev == null ? "—" : fmt(prev)}</TdNum>
                        {rollPieceLabels.map(lbl => <TdNum key={lbl} style={{ color: pm[lbl] == null ? "var(--color-text-tertiary)" : "inherit" }}>{pm[lbl] == null ? "—" : fmt(pm[lbl])}</TdNum>)}
                        <TdNum style={{ color: net != null && net < 0 ? "#0F6E56" : net != null && net > 0 ? "#993C1D" : "inherit" }}>{net == null ? "—" : fmt(net)}</TdNum>
                        <TdNum>{curBal == null ? "—" : fmt(curBal)}</TdNum>
                        <TdNum style={{ color: "var(--color-text-secondary)" }}>{priorEnd == null ? "—" : fmt(priorEnd)}</TdNum>
                        <TdNum>{reqFunds == null ? "—" : fmt(reqFunds)}</TdNum>
                        <TdNum style={{ fontWeight: 600 }}>{totalAfter == null ? "—" : fmt(totalAfter)}</TdNum>
                        <TdNum style={{ color: exc != null && parseFloat(exc) < 0 ? "#993C1D" : "inherit" }}>{exc == null ? "—" : fmt(exc)}</TdNum>
                        <td style={{ padding: "8px 12px", textAlign: "center", fontSize: 13 }}>
                          {tie == null ? <span style={{ color: "var(--color-text-tertiary)" }}>—</span>
                            : tie ? <span style={{ color: "#0F6E56", fontWeight: 700 }} title="Previous Balance + Loan Advances since prior BBC ties to prior period's ending">✓</span>
                            : <span style={{ color: "#993C1D", fontWeight: 700 }} title={"Off by " + fmt(Math.abs(tieExpected - parseFloat(priorEnd)))}>≠</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ padding: "8px 12px 12px", fontSize: 11, color: "var(--color-text-secondary)" }}>
              Columns between Previous Balance and Total After Requested Funds are the certificate's own rollforward pieces (collections, paydowns, adjustments, advances, …). <b>Ties?</b>: each period's <b>Previous Balance + Loan Advances since prior BBC</b> should equal the prior period's ending (Total Loan Balance After Requested Funds); a ≠ flags a rollforward break (missed period, restated balance, or a data issue).
            </div>
          </Card>
        )}

        </>)}

        {/* ══════════ A/R DETAIL — A/R Aging · A/R Aging Trend · Rollforward A/R ══════════ */}
        {tab === "ar" && (<>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "start", marginBottom: 16 }}>
            <div style={{ flex: "0 1 380px", minWidth: 280 }}>
                          <Card style={{ padding: 16 }}>
              <div style={cardTitle}>A/R Aging (invoice date) <span style={{ fontWeight: 400, color: "var(--color-text-secondary)" }}>· {fmtDate(latest.asofdate)}</span></div>
              {arSnap <= 0
                ? <div style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>No A/R aging detail on the latest certificate.</div>
                : <>
                  <div style={{ display: "flex", height: 12, borderRadius: 6, overflow: "hidden", marginBottom: 14, background: "var(--color-background-secondary)" }}>
                    {AGING.map(b => { const v = parseFloat(latest["ar_" + b.key]) || 0; return v > 0 && <div key={b.key} style={{ width: (v / arSnap * 100) + "%", background: b.color }} title={`${b.label}: ${fmt(v)}`} />; })}
                  </div>
                  {AGING.map(b => {
                    const v = parseFloat(latest["ar_" + b.key]) || 0;
                    return (
                      <div key={b.key} style={{ ...row, alignItems: "center" }}>
                        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ width: 10, height: 10, borderRadius: 2, background: b.color, display: "inline-block" }} />
                          {b.label}
                        </span>
                        <span><b>{fmt(v)}</b> <span style={{ color: "var(--color-text-secondary)" }}>({arSnap ? Math.round(v / arSnap * 100) : 0}%)</span></span>
                      </div>
                    );
                  })}
                </>}
            </Card>

            </div>
            <div style={{ flex: 1, minWidth: 320 }}>
                      {/* A/R Aging Trend — invoice-date aging buckets over time (stacked; the buckets partition the balance) */}
        {hist.length > 1 && (
          <Card style={{ marginBottom: 16 }}>
            <div style={{ ...cardTitle, padding: "12px 12px 8px", marginBottom: 0 }}>A/R Aging Trend <span style={{ fontWeight: 400, color: "var(--color-text-secondary)" }}>({hist.length})</span></div>
            <div style={{ padding: "8px 12px 4px" }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                {AGING.map(b => (
                  <button key={b.key} onClick={() => setTrendArAgeOn(o => ({ ...o, [b.key]: !o[b.key] }))}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 8px", borderRadius: 12, border: "0.5px solid var(--color-border-secondary)", background: "none", cursor: "pointer", fontSize: 11, color: "var(--color-text-secondary)", opacity: trendArAgeOn[b.key] ? 1 : 0.4 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: b.color, display: "inline-block" }} />
                    {b.label}
                  </button>
                ))}
              </div>
              <StackTrend key={"aging-" + acct} rows={hist.slice().reverse()} buckets={AGING.filter(b => trendArAgeOn[b.key])} value={(r, b) => parseFloat(r["ar_" + b.key]) || 0} />
            </div>
            <div style={{ maxHeight: 220, overflowY: "auto", overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, whiteSpace: "nowrap" }}>
                <thead><tr>
                  <th style={{ ...thSticky, textAlign: "left" }}>As-of</th>
                  {AGING.map(b => <th key={b.key} style={{ ...thSticky, textAlign: "right" }}>{b.label}</th>)}
                  <th style={{ ...thSticky, textAlign: "right" }}>Total</th>
                </tr></thead>
                <tbody>
                  {hist.map(r => {
                    const tot = AGING.reduce((s, b) => s + (parseFloat(r["ar_" + b.key]) || 0), 0);
                    return (
                      <tr key={r.id} style={{ cursor: "pointer", background: r.id === h.id ? B.cyanLight : "" }} onClick={() => go("collateral-bbc", { bbid: r.id })}>
                        <Td style={{ fontWeight: r.id === h.id ? 600 : 400 }}>{fmtDate(r.asofdate)}</Td>
                        {AGING.map(b => { const v = parseFloat(r["ar_" + b.key]) || 0; return <TdNum key={b.key}>{fmt(v)}<span style={{ fontSize: 10, color: "var(--color-text-secondary)" }}> ({tot ? (v / tot * 100).toFixed(1) : "0.0"}%)</span></TdNum>; })}
                        <TdNum style={{ fontWeight: 600 }}>{fmt(tot)}</TdNum>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}

            </div>
          </div>
        {/* MoM / QoQ / YoY on every A/R line, and the roll (step-down) — arTrendCards.js */}
        <ArPeriodCard hist={hist} />
        <ArRollCard accountid={acct} />
        {/* A/R Trend and Rollforward A/R — shared with the certificate page (collateralCards.js) */}
        <ArTrendCard key={"artrend-" + acct} hist={hist} currentId={h.id} onOpen={id => go("collateral-bbc", { bbid: id })} />
        <ArRollforwardCard hist={hist} currentId={h.id} onOpen={id => go("collateral-bbc", { bbid: id })} />
        <ArQualityCard hist={hist} currentId={h.id} onOpen={id => go("collateral-bbc", { bbid: id })} />

        {/* Top 10 Debtors — open A/R trend (top 10 from the most recent BBC) */}
        {partyTrendCard("Top 10 Debtors — Open A/R", debtorTrend, topDebtorOff, setTopDebtorOff, "Total A/R")}

        {/* Debtors — Open A/R detail (most recent BBC) */}
        {byDebtor.length > 0 && (
          <Card style={{ overflowX: "auto", marginBottom: 16 }}>
            <div style={{ ...cardTitle, padding: "12px 12px 8px", marginBottom: 0, display: "flex", alignItems: "center", gap: 10 }}>
              <span>Debtors — Open A/R <span style={{ fontWeight: 400, color: "var(--color-text-secondary)" }}>({shownDebtors.length}{dbDesig ? ` of ${byDebtor.length}` : ""} · as of {fmtDate(latest.asofdate)})</span></span>
              {dbDesigs.length > 0 && <select value={dbDesig} onChange={e => setDbDesig(e.target.value)} style={{ ...desigSelStyle, marginLeft: "auto", textTransform: "none" }}>
                <option value="">All designations</option>
                {dbDesigs.map(dg => <option key={dg} value={dg}>{dg}</option>)}
              </select>}
            </div>
            <RowCapScroll skip={1}>
              <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, whiteSpace: "nowrap" }}>
                <thead><tr>
                  <th style={{ ...thSticky, textAlign: "left" }}>Debtor (Consolidated)</th>
                  {dbDesigs.length > 0 && <th style={{ ...thSticky, textAlign: "left" }}>Designation</th>}
                  <th style={{ ...thSticky, textAlign: "right" }}>Open Balance</th>
                  <th style={{ ...thSticky, textAlign: "right" }}>% of A/R</th>
                  {AGING.map(b => <th key={b.key} style={{ ...thSticky, textAlign: "right" }}>{b.label}</th>)}
                  <th style={{ ...thSticky, textAlign: "right" }}>Past Due</th>
                  <th style={{ ...thSticky, textAlign: "right" }}>Eligible</th>
                </tr></thead>
                <tbody>
                  {(() => {
                    const arEnd = parseFloat(latest.ar_ending_balance) || 0;
                    const t = shownDebtors.reduce((a, d) => { a.open += parseFloat(d.open_balance) || 0; a.past += parseFloat(d.past_due) || 0; a.elig += parseFloat(d.eligible) || 0; AGING.forEach(b => a[b.key] += parseFloat(d[b.key]) || 0); return a; }, { open: 0, past: 0, elig: 0, inv_0_30: 0, inv_31_60: 0, inv_61_90: 0, inv_91_120: 0, inv_over120: 0 });
                    return [
                      <tr key="tot" style={{ background: "#FDF0D5" }}>
                        <Td style={goldTot}>Total ({shownDebtors.length})</Td>
                        {dbDesigs.length > 0 && <Td style={goldTot}>{dbDesig || "All"}</Td>}
                        <TdNum style={goldTot}>{fmt(t.open)}</TdNum>
                        <TdNum style={goldTot}>{arEnd ? (t.open / arEnd * 100).toFixed(1) + "%" : "—"}</TdNum>
                        {AGING.map(b => <TdNum key={b.key} style={goldTot}>{fmt(t[b.key])}</TdNum>)}
                        <TdNum style={goldTot}>{fmt(t.past)}</TdNum>
                        <TdNum style={goldTot}>{fmt(t.elig)}</TdNum>
                      </tr>,
                      ...shownDebtors.map((d, i) => { const ob = parseFloat(d.open_balance) || 0; return (
                        <tr key={i} onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"} onMouseLeave={e => e.currentTarget.style.background = ""}>
                          <Td style={{ fontWeight: 500 }}>{d.consolidatedname}</Td>
                          {dbDesigs.length > 0 && <Td>{d.designation || "—"}</Td>}
                          <TdNum>{fmt(ob)}</TdNum>
                          <TdNum>{arEnd ? (ob / arEnd * 100).toFixed(1) + "%" : "—"}</TdNum>
                          {AGING.map(b => <TdNum key={b.key}>{fmt(d[b.key] || 0)}</TdNum>)}
                          <TdNum>{fmt(d.past_due || 0)}</TdNum>
                          <TdNum>{fmt(d.eligible || 0)}</TdNum>
                        </tr>
                      ); })
                    ];
                  })()}
                </tbody>
              </table>
            </RowCapScroll>
          </Card>
        )}

        </>)}

        {/* ══════════ A/P DETAIL — A/P Aging · A/P Aging Trend ══════════ */}
        {tab === "ap" && (<>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "start", marginBottom: 16 }}>
            <div style={{ flex: "0 1 380px", minWidth: 280 }}>
              {hasAP
                ?               <Card style={{ padding: 16 }}>
                <div style={cardTitle}>A/P Aging (due date) <span style={{ fontWeight: 400, color: "var(--color-text-secondary)" }}>· {fmtDate(latest.asofdate)}</span></div>
                {apSnap <= 0
                  ? <div style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>No A/P aging detail on the latest certificate.</div>
                  : <>
                    <div style={{ display: "flex", height: 12, borderRadius: 6, overflow: "hidden", marginBottom: 14, background: "var(--color-background-secondary)" }}>
                      {APAGING.map(b => { const v = parseFloat(latest[b.key]) || 0; return v > 0 && <div key={b.key} style={{ width: (v / apSnap * 100) + "%", background: b.color }} title={`${b.label}: ${fmt(v)}`} />; })}
                    </div>
                    {APAGING.map(b => {
                      const v = parseFloat(latest[b.key]) || 0;
                      return (
                        <div key={b.key} style={{ ...row, alignItems: "center" }}>
                          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ width: 10, height: 10, borderRadius: 2, background: b.color, display: "inline-block" }} />
                            {b.label}
                          </span>
                          <span><b>{fmt(v)}</b> <span style={{ color: "var(--color-text-secondary)" }}>({apSnap ? Math.round(v / apSnap * 100) : 0}%)</span></span>
                        </div>
                      );
                    })}
                  </>}
              </Card>

                : <Card style={{ padding: 16 }}><div style={cardTitle}>A/P Aging (due date)</div><div style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>No A/P Waterfall on this account's certificates.</div></Card>}
            </div>
            <div style={{ flex: 1, minWidth: 320 }}>
                      {/* A/P Aging Trend — due-date aging buckets over time (stacked; the buckets partition the balance) */}
        {hasAP && hist.length > 1 && (
          <Card style={{ marginBottom: 16 }}>
            <div style={{ ...cardTitle, padding: "12px 12px 8px", marginBottom: 0 }}>A/P Aging Trend <span style={{ fontWeight: 400, color: "var(--color-text-secondary)" }}>({hist.length})</span></div>
            <div style={{ padding: "8px 12px 4px" }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                {APAGING.map(b => (
                  <button key={b.key} onClick={() => setTrendApOn(o => ({ ...o, [b.key]: !o[b.key] }))}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 8px", borderRadius: 12, border: "0.5px solid var(--color-border-secondary)", background: "none", cursor: "pointer", fontSize: 11, color: "var(--color-text-secondary)", opacity: trendApOn[b.key] ? 1 : 0.4 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: b.color, display: "inline-block" }} />
                    {b.label}
                  </button>
                ))}
              </div>
              <StackTrend key={"apaging-" + acct} rows={hist.slice().reverse()} buckets={APAGING.filter(b => trendApOn[b.key])} value={(r, b) => parseFloat(r[b.key]) || 0} />
            </div>
            {/* What changed — the A/P aging mix shifting out (same rules as the Insights checks AP aging shift / drift) */}
            <ApAgingFindings hist={hist} asOf={null} th={apTh} />
            <div style={{ maxHeight: 220, overflowY: "auto", overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, whiteSpace: "nowrap" }}>
                <thead><tr>
                  <th style={{ ...thSticky, textAlign: "left" }}>As-of</th>
                  {APAGING.map(b => <th key={b.key} style={{ ...thSticky, textAlign: "right" }}>{b.label}</th>)}
                  <th style={{ ...thSticky, textAlign: "right" }}>Total</th>
                </tr></thead>
                <tbody>
                  {hist.map(r => {
                    const tot = APAGING.reduce((s, b) => s + (parseFloat(r[b.key]) || 0), 0);
                    return (
                      <tr key={r.id} style={{ cursor: "pointer", background: r.id === h.id ? B.cyanLight : "" }} onClick={() => go("collateral-bbc", { bbid: r.id })}>
                        <Td style={{ fontWeight: r.id === h.id ? 600 : 400 }}>{fmtDate(r.asofdate)}</Td>
                        {APAGING.map(b => { const v = parseFloat(r[b.key]) || 0; return <TdNum key={b.key}>{fmt(v)}<span style={{ fontSize: 10, color: "var(--color-text-secondary)" }}> ({tot ? (v / tot * 100).toFixed(1) : "0.0"}%)</span></TdNum>; })}
                        <TdNum style={{ fontWeight: 600 }}>{fmt(tot)}</TdNum>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}

            </div>
          </div>

        {/* Top 10 Vendors — open A/P trend (top 10 from the most recent BBC) */}
        {partyTrendCard("Top 10 Vendors — Open A/P", vendorTrend, topVendorOff, setTopVendorOff, "Total A/P")}

        {/* A/P by Vendor detail (most recent BBC) */}
        {byVendorAP.length > 0 && (() => {
          const t = shownVendors.reduce((a, d) => { a.open += parseFloat(d.open_balance) || 0; VBKT.forEach(b => a[b.key] += parseFloat(d[b.key]) || 0); return a; }, { open: 0, dd_current: 0, dd_1_30: 0, dd_31_60: 0, dd_61_90: 0, dd_over90: 0 });
          return (
            <Card style={{ overflowX: "auto", marginBottom: 16 }}>
              <div style={{ ...cardTitle, padding: "12px 12px 8px", marginBottom: 0, display: "flex", alignItems: "center", gap: 10 }}>
                <span>A/P by Vendor <span style={{ fontWeight: 400, color: "var(--color-text-secondary)" }}>({shownVendors.length}{vnDesig ? ` of ${byVendorAP.length}` : ""} · as of {fmtDate(latest.asofdate)})</span></span>
                {vnDesigs.length > 0 && <select value={vnDesig} onChange={e => setVnDesig(e.target.value)} style={{ ...desigSelStyle, marginLeft: "auto", textTransform: "none" }}>
                  <option value="">All designations</option>
                  {vnDesigs.map(dg => <option key={dg} value={dg}>{dg}</option>)}
                </select>}
              </div>
              <div style={{ maxHeight: 420, overflowY: "auto", overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, whiteSpace: "nowrap" }}>
                  <thead><tr>
                    <th style={{ ...thSticky, textAlign: "left" }}>Vendor (Consolidated)</th>
                    {vnDesigs.length > 0 && <th style={{ ...thSticky, textAlign: "left" }}>Designation</th>}
                    <th style={{ ...thSticky, textAlign: "right" }}>Open Balance</th>
                    <th style={{ ...thSticky, textAlign: "right" }}>% of A/P</th>
                    {VBKT.map(b => <th key={b.key} style={{ ...thSticky, textAlign: "right" }}>{b.label}</th>)}
                  </tr></thead>
                  <tbody>
                    <tr style={{ background: "#FDF0D5" }}>
                      <Td style={goldTot}>Total ({shownVendors.length})</Td>
                      {vnDesigs.length > 0 && <Td style={goldTot}>{vnDesig || "All"}</Td>}
                      <TdNum style={goldTot}>{fmt(t.open)}</TdNum>
                      <TdNum style={goldTot}>{t.open ? "100.0%" : "—"}</TdNum>
                      {VBKT.map(b => <TdNum key={b.key} style={goldTot}>{fmt(t[b.key])}</TdNum>)}
                    </tr>
                    {shownVendors.map((d, i) => { const ob = parseFloat(d.open_balance) || 0; return (
                      <tr key={i} onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"} onMouseLeave={e => e.currentTarget.style.background = ""}>
                        <Td style={{ fontWeight: 500 }}>{d.consolidatedname}</Td>
                        {vnDesigs.length > 0 && <Td>{d.designation || "—"}</Td>}
                        <TdNum>{fmt(ob)}</TdNum>
                        <TdNum>{t.open ? (ob / t.open * 100).toFixed(1) + "%" : "—"}</TdNum>
                        {VBKT.map(b => <TdNum key={b.key}>{fmt(d[b.key] || 0)}</TdNum>)}
                      </tr>
                    ); })}
                  </tbody>
                </table>
              </div>
            </Card>
          );
        })()}
        </>)}

        {/* ══════════ INVENTORY ══════════════════════════════════════════════
            Account-level, across every certificate on file. The per-category
            rows behind this have been written on every upload since the parser
            learned the category columns and were never once read across dates —
            so the mix chart below is showing something that existed in the
            database and had no way out of it. */}
        {/* Inventory — the same component the certificate page renders (collateralCards.js) */}
        {tab === "sm" && smCert && <>
          {latest && smCert.id !== latest.id && (
            <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 12 }}>
              The latest certificate has no slow-moving sheet. Showing the most recent one that does, dated {fmtDate(smCert.asofdate)}.
            </div>
          )}
          <SlowMovingTab key={"sm-" + acct} cur={smCert} smLines={smLines} skus={partyTrend.skus}
                         onOpen={id => go("collateral-bbc", { bbid: id })} />
        </>}
        {tab === "inv" && <InventoryTab key={"inv-" + acct} invTrend={invTrend} hist={hist} skus={partyTrend.skus} cur={latest} onOpen={id => go("collateral-bbc", { bbid: id })} />}


        </div>
        </div>

        {hist.length === 1 && (
          <Card style={{ padding: 20, textAlign: "center", color: "var(--color-text-secondary)", fontSize: 13 }}>
            Only one borrowing base on file — trends appear once a second period is uploaded.
          </Card>
        )}
      </>}
  </>;
};
